import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';

interface AnimatedCounterProps {
  value: number | string | undefined | null;
  duration?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  decimals?: number;
}

export const AnimatedCounter: React.FC<AnimatedCounterProps> = ({
  value,
  duration = 2,
  prefix = '',
  suffix = '',
  className = '',
  decimals = 0,
}) => {
  const [count, setCount] = useState(0);
  const [ref, inView] = useIntersectionObserver({ threshold: 0.1 });

  // Convert value to number, handling different input types
  const numericValue = React.useMemo(() => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      // Remove any non-numeric characters except decimal point and minus sign
      const cleanedValue = value.replace(/[^\d.-]/g, '');
      const parsed = parseFloat(cleanedValue);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }, [value]);

  useEffect(() => {
    if (numericValue !== null) {
      let startTime: number;
      let animationFrame: number;

      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);

        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = numericValue * easeOutQuart;

        setCount(currentValue);

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        }
      };

      // Start animation with a small delay to ensure component is mounted
      const timeoutId = setTimeout(() => {
        animationFrame = requestAnimationFrame(animate);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }
  }, [numericValue, duration]);

  const formatNumber = (num: number) => {
    // Handle floating point numbers
    if (decimals > 0 || num % 1 !== 0) {
      return num.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: Math.max(decimals, 2),
      });
    }

    // Handle large integers with appropriate formatting
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }

    return num.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  // Check if the original value is a non-numeric string that should be displayed as-is
  const isNonNumericString = typeof value === 'string' && isNaN(parseFloat(value.replace(/[^\d.-]/g, '')));

  return (
    <motion.span
      ref={ref}
      className={className}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: inView ? 1 : 0, scale: inView ? 1 : 0.5 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      {prefix}
      {isNonNumericString ? value : formatNumber(count)}
      {suffix}
    </motion.span>
  );
};